const apiBase = process.env.REACT_APP_API_REQUEST

/**
 * Obtain an access token for an anonymous (guest) user.
 * For the global site there is no SSO — every user is a guest identified by UUID + guestSecret.
 *
 * Uses native fetch to avoid the axios interceptor loop.
 *
 * @param {string|null} uuid - Existing anonymous uuid from localStorage (optional).
 * @param {string|null} guestSecret - Guest credential from localStorage (optional).
 * @returns {Promise<{ token: string|null, uuid: string|null, guestSecret: string|null }>}
 */
export default async function getGuestToken(uuid = null, guestSecret = null) {
  const requestGuestToken = async body => {
    const res = await fetch(`${apiBase}/auth/guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      return { token: null, uuid: null, guestSecret: null, failed: true }
    }
    return {
      token: data?.token || null,
      uuid: data?.uuid || body.uuid || null,
      guestSecret: data?.guestSecret || null,
      failed: false,
    }
  }

  try {
    const body = {}
    if (uuid) body.uuid = uuid
    if (guestSecret) body.guestSecret = guestSecret

    let result = await requestGuestToken(body)

    // Lost/mismatched secret (or legacy client after migrate): start a fresh guest.
    if ((!result.token || result.failed) && uuid) {
      result = await requestGuestToken({})
    }

    return {
      token: result.token,
      uuid: result.uuid,
      guestSecret: result.guestSecret,
    }
  } catch (e) {
    console.warn('getGuestToken failed:', e)
    return { token: null, uuid: null, guestSecret: null }
  }
}
